import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useMyDailyDirectClient, track } from "@mydailydirect/app-kit";
import {
  Badge,
  Button,
  Card,
  CardContent,
  formatPaise,
  Skeleton,
  MDD_SUPPORT_PHONE_DISPLAY,
  MDD_SUPPORT_WHATSAPP_URL,
} from "@mydailydirect/ui";
import {
  Store,
  ArrowRight,
  ClipboardList,
  CheckCircle2,
  Truck,
  ShieldCheck,
  Receipt,
  Clock,
  Sparkles,
  MessageSquare,
  TrendingUp,
} from "lucide-react";
import type { OrderDTO } from "@mydailydirect/api-client";
import { useRetailerId } from "../lib/useRetailerId";

export function OrderSuccessPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);
  const location = useLocation();
  const client = useMyDailyDirectClient();
  const { buyerName, buyerPhone } = useRetailerId();

  // Try to use order passed via navigation state for instant zero-latency render
  const stateOrder = (location.state as { order?: OrderDTO } | undefined)?.order;

  const orderQuery = useQuery({
    queryKey: ["orders", orderId],
    queryFn: () => client.orders.get(orderId),
    initialData: stateOrder?.id === orderId ? stateOrder : undefined,
    enabled: Number.isFinite(orderId) && orderId > 0,
    retry: 2,
  });

  const skusQuery = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
    staleTime: 60_000,
  });

  const order = orderQuery.data ?? stateOrder;
  const lines = order?.lines ?? [];
  const totalUnits = lines.reduce((sum, l) => sum + l.qty, 0);

  const trackedRef = useRef(false);
  useEffect(() => {
    if (order?.id && !trackedRef.current) {
      trackedRef.current = true;
      track("order_placed", {
        orderId: order.id,
        valuePaise: order.totalPaise,
        retailerId: order.retailerId ?? undefined,
        propsJson: {
          itemCount: order.lines?.length ?? 0,
          paymentMethod: order.paymentMethod,
        },
      });
    }
  }, [order?.id, order?.totalPaise, order?.retailerId]);

  const skuName = (skuId: number) =>
    skusQuery.data?.find((s) => s.id === skuId)?.name ?? `SKU #${skuId}`;

  const paymentMethodLabel = (() => {
    const method = (order?.paymentMethod || "COD").toUpperCase();
    if (method === "UPI" || method === "QR") return "UPI QR (Paid Upfront)";
    if (method === "CREDIT") return "Credit Account (Khata)";
    return "Pay on Delivery (Cash / UPI QR to Driver)";
  })();

  const waOrderText = encodeURIComponent(
    `✅ *ORDER #${order?.id ?? orderId} CONFIRMED - MYDAILYDIRECT*\n` +
      `🏪 Shop: ${buyerName}${buyerPhone ? ` (${buyerPhone})` : ""}\n` +
      `📦 Items:\n` +
      lines
        .map((l) => `• ${skuName(l.skuId)} x ${l.qty}`)
        .join("\n") +
      `\n💰 Total: ${formatPaise(order?.totalPaise ?? 0)} (${order?.paymentMethod || "COD"})\n` +
      `🚚 Slot: Morning Run (6:00 AM - 9:00 AM)\n` +
      `📞 For Help / Support: Call or WhatsApp ${MDD_SUPPORT_PHONE_DISPLAY}`
  );

  return (
    <div className="max-w-3xl mx-auto px-2 sm:px-6 py-3 sm:py-6 space-y-5 pb-24">
      {/* Top Navigation Bar: Back to Catalog / Home */}
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="font-bold text-xs sm:text-sm text-muted-foreground hover:text-foreground gap-1.5 rounded-control -ml-2"
        >
          <Store className="h-4 w-4 text-cta" /> Back to Catalog
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/orders")}
          className="font-bold text-xs gap-1.5 rounded-control border-border hover:bg-muted"
        >
          <ClipboardList className="h-3.5 w-3.5" /> My Orders History
        </Button>
      </div>

      {/* Main Celebratory Card */}
      <Card className="overflow-hidden border border-tone-success-border bg-card shadow-sm rounded-card">
        {/* Top Celebration Hero Graphic Banner */}
        <div className="relative bg-tone-success-soft/40 p-5 sm:p-8 text-center flex flex-col items-center border-b border-tone-success-border/30">
          {/* Brand Logo & Tagline */}
          <div className="flex items-center gap-2 mb-3">
            <img
              src="/my-daily-direct-logo.jpg"
              alt="My Daily Direct"
              className="h-8 w-auto rounded-control shadow-2xs object-contain bg-white p-0.5"
              onError={(e) => {
                (e.target as HTMLElement).style.display = "none";
              }}
            />
            <span className="font-black text-xs sm:text-sm tracking-tight text-foreground uppercase">
              My Daily Direct
            </span>
            <Badge variant="success" className="text-2xs uppercase tracking-wider font-black ml-1">
              Confirmed
            </Badge>
          </div>

          {/* Celebratory Image */}
          <div className="relative my-2 w-full max-w-sm rounded-control overflow-hidden shadow-xs border border-tone-success-border/40 bg-background/50">
            <img
              src="/order-success.png"
              alt="Order Placed Successfully - My Daily Direct Wholesale Delivery"
              className="w-full h-auto aspect-square object-cover"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                if (!img.src.endsWith(".jpg")) {
                  img.src = "/order-success.jpg";
                }
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-4">
              <div className="text-left text-white">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-cta text-2xs font-black uppercase tracking-wider text-cta-foreground">
                  <Sparkles className="w-3 h-3 text-cta-foreground" /> Direct Mandi Dispatch
                </span>
                <p className="text-xs sm:text-sm font-bold mt-1 text-white/95">
                  Fresh Wholesale Stock Confirmed
                </p>
              </div>
            </div>
          </div>

          {/* Celebration Headings */}
          <div className="mt-4 space-y-1">
            <div className="inline-flex items-center gap-2 text-tone-success-text font-black text-lg sm:text-2xl type-title">
              <CheckCircle2 className="w-6 h-6 shrink-0" />
              <span>Wholesale Order Placed Successfully!</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto">
              Your order has been recorded with <strong>My Daily Direct</strong>. Fresh stock will be dispatched in our morning delivery run.
            </p>
          </div>
        </div>

        {/* Order Details & Summary Section */}
        <CardContent className="p-4 sm:p-7 space-y-5 bg-card">
          {orderQuery.isLoading && !order ? (
            <div className="space-y-3">
              <Skeleton className="h-16 rounded-card" />
              <Skeleton className="h-24 rounded-card" />
            </div>
          ) : (
            <>
              {/* Highlight Metrics Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
                <div className="p-3 sm:p-3.5 rounded-control bg-sunken border border-border">
                  <span className="text-2xs text-muted-foreground uppercase font-extrabold block mb-1">
                    Order Ref
                  </span>
                  <span className="text-sm sm:text-lg font-black text-foreground block font-mono">
                    #{order?.id ?? orderId}
                  </span>
                </div>

                <div className="p-3 sm:p-3.5 rounded-control bg-tone-success-soft border border-tone-success-border">
                  <span className="text-2xs text-tone-success-text uppercase font-extrabold block mb-1">
                    Total Amount
                  </span>
                  <span className="text-sm sm:text-lg font-black text-tone-success-text block type-num">
                    {formatPaise(order?.totalPaise ?? 0)}
                  </span>
                </div>

                <div className="p-3 sm:p-3.5 rounded-control bg-sunken border border-border">
                  <span className="text-2xs text-muted-foreground uppercase font-extrabold block mb-1">
                    Delivery Slot
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-foreground flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-cta shrink-0" /> Morning Run
                  </span>
                  <span className="text-2xs text-muted-foreground block mt-0.5">
                    6:00 AM – 9:00 AM
                  </span>
                </div>

                <div className="p-3 sm:p-3.5 rounded-control bg-sunken border border-border">
                  <span className="text-2xs text-muted-foreground uppercase font-extrabold block mb-1">
                    Payment Method
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-foreground block truncate">
                    {order?.paymentMethod || "COD"}
                  </span>
                  <span className="text-2xs text-muted-foreground block mt-0.5 truncate">
                    {paymentMethodLabel}
                  </span>
                </div>
              </div>

              {/* Items Snapshot Card */}
              {lines.length > 0 && (
                <div className="rounded-control border border-border bg-sunken p-3.5 sm:p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-2xs font-extrabold text-muted-foreground uppercase tracking-wider">
                      Ordered Items ({lines.length} items · {totalUnits} units)
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/orders/${order?.id ?? orderId}`)}
                      className="text-xs font-bold text-cta h-7 px-2 hover:bg-muted"
                    >
                      Full Details <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </div>

                  <div className="divide-y divide-border">
                    {lines.slice(0, 4).map((line) => (
                      <div key={line.id ?? line.skuId} className="py-2 flex items-center justify-between text-xs">
                        <span className="font-bold text-foreground truncate max-w-[180px] sm:max-w-xs">
                          {skuName(line.skuId)}
                        </span>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-muted-foreground font-medium">Qty: <strong className="font-mono text-foreground">{line.qty}</strong></span>
                          <span className="font-black text-foreground type-num">
                            {line.lineTotalPaise != null ? formatPaise(line.lineTotalPaise) : "—"}
                          </span>
                        </div>
                      </div>
                    ))}
                    {lines.length > 4 && (
                      <div className="pt-2 text-2xs text-muted-foreground text-center font-medium">
                        + {lines.length - 4} more items. Click "View Order Details" below to view full invoice.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Retailer Mandi Assurances */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                <div className="flex items-start gap-2.5 p-3 rounded-control bg-card border border-border">
                  <div className="w-7 h-7 rounded-lg bg-tone-success-soft text-tone-success-text flex items-center justify-center shrink-0">
                    <Truck className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="font-bold text-foreground block">Trusted Sourcing</span>
                    <span className="text-2xs text-muted-foreground">Rigorous QC & quality-tested fresh stock at wholesale rates</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 p-3 rounded-control bg-card border border-border">
                  <div className="w-7 h-7 rounded-lg bg-tone-success-soft text-tone-success-text flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="font-bold text-foreground block">Quality Guarantee</span>
                    <span className="text-2xs text-muted-foreground">Inspect crates at shop; instant adjustment</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 p-3 rounded-control bg-card border border-border">
                  <div className="w-7 h-7 rounded-lg bg-tone-success-soft text-tone-success-text flex items-center justify-center shrink-0">
                    <Receipt className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="font-bold text-foreground block">Digital Invoice</span>
                    <span className="text-2xs text-muted-foreground">Official GST bill generated with delivery</span>
                  </div>
                </div>
              </div>

              {/* Instagram APMC Commodity Trend & WhatsApp Fast-Track Card */}
              <div className="p-3.5 sm:p-4 rounded-control border border-border bg-sunken space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-1.5 text-xs font-black text-foreground">
                      <TrendingUp className="h-4 w-4 text-cta shrink-0" />
                      <span>Trusted Sourcing & Quality-Tested Wholesale Updates</span>
                    </div>
                    <p className="text-2xs text-muted-foreground leading-relaxed">
                      Built for Hotels, PGs, Bakeries, Retailers & Traders — follow <strong>@mydailydirect.in</strong> for daily wholesale rates, quality-tested staple updates & <strong>Lowest Price Guarantee</strong>.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <a
                    href="https://www.instagram.com/mydailydirect.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-control bg-sunken hover:bg-muted text-foreground text-xs font-bold border border-border transition-colors"
                  >
                    <span>📸 Follow @mydailydirect.in</span>
                  </a>
                  <a
                    href={`${MDD_SUPPORT_WHATSAPP_URL}?text=${waOrderText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-control border border-tone-success-border bg-tone-success-soft text-tone-success-text text-xs font-bold hover:brightness-95 transition-all"
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span>Send Order Slip on WhatsApp</span>
                  </a>
                </div>
              </div>
            </>
          )}

          {/* Primary Action Buttons */}
          <div className="pt-2 flex flex-col sm:flex-row items-center gap-2.5">
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate("/")}
              className="w-full sm:flex-1 h-12 text-sm sm:text-base font-black shadow-md rounded-control bg-cta text-cta-foreground hover:brightness-105 active:scale-[0.99]"
            >
              <Store className="h-5 w-5 mr-2" /> Back to Catalog Listing
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate(`/orders/${order?.id ?? orderId}`)}
              className="w-full sm:flex-1 h-12 text-sm sm:text-base font-bold rounded-control border-border hover:bg-muted"
            >
              View Order Details <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
